import { LuLock } from "react-icons/lu";
import RadioButtonControlGroup from "../../shared/RadioButtonControlGroup";
import "./TabBar.scss";

/**
 * Plan Manager tab bar with optional trailing context label. Disabled tabs
 * render with a pending tone, lock icon, and cannot be selected. Pure presentational.
 *
 * @param {object} props
 * @param {string} props.ariaLabel - Accessible label for the tab list.
 * @param {string} props.selectedTabId - Currently selected tab value.
 * @param {(tabId: string) => void} props.onTabChange - Emits the newly selected tab.
 * @param {Array<{
 *   value: string,
 *   label: string,
 *   enabled?: boolean,
 *   disabledTooltip?: string,
 *   id?: string,
 *   controls?: string,
 * }>} props.tabs - Tab definitions; `enabled` defaults to true.
 * @param {string} [props.trailingLabel] - Optional context shown after the tabs.
 * @param {string} [props.minCh] - Min width per tab segment.
 * @param {string} [props.className] - Extra class on the root element.
 * @returns {import('react').JSX.Element}
 */
const TabBar = ({
    ariaLabel,
    selectedTabId,
    onTabChange,
    tabs,
    trailingLabel,
    minCh = "14ch",
    className,
}) => {
    const tabOptions = tabs.map((tab) => {
        const isEnabled = tab.enabled !== false;
        const labelNode = isEnabled ? (
            tab.label
        ) : (
            <span className="plan-manager-tab-bar__tab-label">
                <LuLock
                    aria-hidden
                    size={12}
                    className="plan-manager-tab-bar__lock-icon"
                />
                <span>{tab.label}</span>
            </span>
        );

        return {
            value: tab.value,
            label: labelNode,
            id: tab.id,
            controls: tab.controls,
            disabled: !isEnabled,
            tooltip: !isEnabled ? tab.disabledTooltip : undefined,
            tone:
                tab.value === selectedTabId
                    ? "default"
                    : !isEnabled
                      ? "pending"
                      : "default",
        };
    });

    const handleTabChange = (nextTabId) => {
        const targetTab = tabs.find((tab) => tab.value === nextTabId);

        if (targetTab && targetTab.enabled === false) {
            return;
        }

        onTabChange(nextTabId);
    };

    const rootClassName = className
        ? `plan-manager-tab-bar ${className}`
        : "plan-manager-tab-bar";

    return (
        <div className={rootClassName}>
            <RadioButtonControlGroup
                role="tablist"
                ariaLabel={ariaLabel}
                value={selectedTabId}
                onChange={handleTabChange}
                options={tabOptions}
                minCh={minCh}
                className="plan-manager-tab-bar__tabs"
            />
            {trailingLabel ? (
                <div className="plan-manager-tab-bar__trailing">{trailingLabel}</div>
            ) : null}
        </div>
    );
};

export default TabBar;
